<template>
    <div
        class="mx-6 mb-12 max-w-screen-xl space-y-5 lg:mx-auto lg:my-12 lg:min-w-[1024px]"
    >
        <div>
            <span class="text-xl text-gray-700">Thanks!</span>
            <div class="my-2 text-4xl font-bold text-gray-900">
                Successful Order
            </div>
            <div
                :style="{
                    height: '3px',
                    background:
                        'linear-gradient(90deg, rgb(var(--color-primary-600)) 0%, rgba(33, 150, 243, 0) 50%)'
                }"
            ></div>
        </div>

        <div
            v-if="invoice !== null"
            class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
        >
            <div class="mb-3 sm:mb-0">
                <span class="mr-2 text-xl font-medium text-gray-900"
                    >Order number:</span
                >
                <span class="text-xl font-medium text-primary-500"
                    >#{{ invoice.referenceNumber }}</span
                >
            </div>
        </div>

        <ul
            v-if="contractsWithoutLineItems.length > 0"
            class="m-0 list-none p-0"
        >
            <contract-line-item-display
                v-for="(contract, contractIndex) in contractsWithoutLineItems"
                :key="contract.id"
                :class="{
                    'border-b-0':
                        contractIndex === contractsWithoutLineItems.length - 1
                }"
                :contract="contract"
            />
        </ul>

        <ul
            v-if="invoice !== null"
            class="m-0 list-none p-0"
        >
            <invoice-line-item-display
                v-for="(lineItem, i) in invoice.lineItems"
                :key="i"
                :class="{
                    'border-b-0': i === invoice.lineItems.length - 1
                }"
                :line-item="lineItem"
                :first-for-contract="
                    firstContractInvoiceItemIds.has(lineItem.id)
                "
            />
        </ul>

        <div class="flex flex-wrap">
            <div
                class="mt-5 flex w-full items-end pl-3 lg:mt-0 lg:w-1/2 lg:pl-0 lg:pr-3"
            >
                <ul class="m-0 w-full list-none space-y-3 p-0">
                    <li>
                        <span class="font-medium text-gray-900">Summary</span>
                    </li>
                    <li class="flex justify-between border-t pt-4">
                        <span class="text-gray-900">Subtotal</span>
                        <span class="text-lg font-medium text-gray-900">{{
                            formattedSubtotal
                        }}</span>
                    </li>
                    <li
                        v-if="inclusiveSalesTax.isNotZero()"
                        class="flex justify-between"
                    >
                        <span class="text-gray-900">Inclusive Tax</span>
                        <span class="text-lg font-medium text-gray-900">{{
                            inclusiveSalesTax.formatCurrency()
                        }}</span>
                    </li>
                    <li class="flex justify-between">
                        <span class="text-gray-900">Tax</span>
                        <span class="text-lg font-medium text-gray-900">{{
                            formattedSalesTaxExclusive
                        }}</span>
                    </li>
                    <li class="flex justify-between border-t py-3">
                        <span class="font-medium text-gray-900">Total</span>
                        <span class="text-lg font-bold text-gray-900">{{
                            formattedTotal
                        }}</span>
                    </li>
                    <template
                        v-if="
                            invoice !== null &&
                            invoice.friendlyBindings.length > 0
                        "
                    >
                        <li class="flex justify-between border-t pt-3">
                            <span class="font-medium text-gray-900"
                                >Payment</span
                            >
                            <span class="text-lg font-bold text-gray-900">{{
                                invoice.friendlyBindings[0].amountAndFee
                            }}</span>
                        </li>
                        <li
                            v-if="
                                invoice.friendlyBindings.length > 1 &&
                                invoice.friendlyBindings[1].isSubItem
                            "
                            class="flex flex-col"
                        >
                            <span class="text-sm text-gray-700">
                                ********** &mdash;
                                {{ invoice.friendlyBindings[1].amount }}
                            </span>
                            <div
                                v-if="invoice.friendlyBindings[1].fee"
                                class="text-500 text-xs"
                                v-html="invoice.friendlyBindings[1].fee"
                            />
                        </li>
                    </template>
                </ul>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const CheckoutSuccessKioskDisplayFragment = graphql(/* GraphQL */ `
    fragment CheckoutSuccessKioskDisplayFragment on CommitCheckoutResult {
        invoice {
            id
            referenceNumber
            lineItems {
                id
                contract {
                    __typename
                    ... on PlanContract {
                        id
                    }
                }
                ...InvoiceLineItemDisplayInvoiceItemFragment
            }
            total
            subTotal
            salesTaxExclusive
            salesTaxInclusive
            friendlyBindings {
                label
                amount
                amountAndFee
                fee
                isSubItem
            }
        }
        contracts {
            ... on PlanContract {
                __typename
                id
                ...ContractLineItemDisplayPlanContractFragment
            }
        }
        ...AnalyticsCommitCheckoutResultFragment
    }
`)
</script>

<script setup lang="ts">
import { useHead } from 'unhead'
import { computed, onMounted } from 'vue'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import InvoiceLineItemDisplay from '~/components/Checkout/InvoiceLineItemDisplay.vue'
import ContractLineItemDisplay from '~/components/Checkout/ContractLineItemDisplay.vue'
import { Decimal } from '~/composables/support/decimal'
import { useLayoutMode } from '~/composables/use-layout-mode'
import { usePurchaseAnalytics } from '~/composables/analytics/purchase-event'

const props = defineProps<{
    checkoutResultData: FragmentType<typeof CheckoutSuccessKioskDisplayFragment>
}>()

const checkoutData = computed(() =>
    getFragmentData(
        CheckoutSuccessKioskDisplayFragment,
        props.checkoutResultData
    )
)
const { isKiosk } = useLayoutMode()
const { emitPurchaseEvent } = usePurchaseAnalytics()

useHead({
    title: 'Success'
})

const invoice = computed(() => {
    return checkoutData.value.invoice
})

const firstContractInvoiceItemIds = computed(() => {
    const invoiceItemIds = new Set()
    const usedContractIds = new Set()

    if (invoice.value !== null) {
        invoice.value.lineItems.forEach(item => {
            if (
                item.contract !== null &&
                item.contract.__typename === 'PlanContract' &&
                !usedContractIds.has(item.contract.id)
            ) {
                usedContractIds.add(item.contract.id)
                invoiceItemIds.add(item.id)
            }
        })
    }

    return invoiceItemIds
})

const formattedSubtotal = computed(() => {
    return invoice.value === null
        ? Decimal.zero().formatCurrency()
        : new Decimal(invoice.value.subTotal).formatCurrency()
})

const inclusiveSalesTax = computed(() =>
    invoice.value === null
        ? Decimal.zero()
        : new Decimal(invoice.value.salesTaxInclusive)
)

const formattedSalesTaxExclusive = computed(() => {
    return invoice.value === null
        ? Decimal.zero().formatCurrency()
        : new Decimal(invoice.value.salesTaxExclusive).formatCurrency()
})

const formattedTotal = computed(() => {
    return invoice.value === null
        ? Decimal.zero().formatCurrency()
        : new Decimal(invoice.value.total).formatCurrency()
})

const contractsWithoutLineItems = computed(() => {
    const usedContractIds = new Set()

    if (invoice.value !== null) {
        invoice.value.lineItems.forEach(item => {
            if (
                item.contract !== null &&
                item.contract.__typename === 'PlanContract'
            ) {
                usedContractIds.add(item.contract.id)
            }
        })
    }

    // Filter to only contracts that have no line items
    return checkoutData.value.contracts.filter(contract => {
        if (contract.__typename === 'PlanContract') {
            if (usedContractIds.has(contract.id)) {
                return false
            } else {
                return true
            }
        }

        return true
    })
})

onMounted(() => {
    emitPurchaseEvent(checkoutData.value)
})
</script>
